import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
/* eslint-disable react/prop-types */
import { translate } from '~l10n/client';

import { roomsKeys } from '~teacher/hook/room/roomsKeys';
import { useMaxRosterSize } from '~teacher/hook/room/useMaxRosterSize';

import Constants from '../../../../../shared/Constants';
import CloseX from '../../../../shared/components/buttons/CloseX';
import Input from '../../../../shared/components/elements/Input';
import Select from '../../../../shared/components/elements/Select';
import Loader from '../../../../shared/components/status/Loader';
import AlertIcon from '../../../../shared/icons/AlertIcon';
import InfoIcon from '../../../../shared/icons/InfoIcon';

let c = null;
let m = null;
let t = null;

interface Props {
  controller: any;
  model: any;
  teacher: any;
}

export default function RosterFileView({ teacher, model, controller }: Props) {
  const queryClient = useQueryClient();
  function renderSettings() {
    let columnOrder = translate('Last Name, First Name, Student ID, Email');
    let fileType = 'CSV';
    let tableClass = 'roster-file-settings-table';

    if (m.importing) {
      columnOrder = translate(
        'Last Name, First Name, Student ID, Email (Optional)'
      );
      if (m.importOrder === Constants.FIRST_LAST_ID) {
        columnOrder = translate(
          'First Name, Last Name, Student ID, Email (Optional)'
        );
      }

      if (m.importType === Constants.XLSX) {
        fileType = 'XLSX';
      }
    } else {
      if (m.exportOrder === Constants.FIRST_LAST_ID) {
        columnOrder = translate('First Name, Last Name, Student ID, Email');
      }

      if (m.exportType === Constants.XLSX) {
        fileType = 'XLSX';
      }

      if (m.showExportSummary) {
        tableClass = 'roster-file-settings-table-export-summary';
      }
    }

    return (
      <table key="modalContentChildren-renderSettings-1" className={tableClass}>
        <tbody>
          <tr>
            <td className="roster-file-settings-descriptor">
              {translate('Column Order') + ':'}
            </td>
            <td className="roster-file-settings-text">{columnOrder}</td>
          </tr>
          <tr>
            <td className="roster-file-settings-descriptor">
              {translate('File Type') + ':'}
            </td>
            <td className="roster-file-settings-text">{fileType}</td>
          </tr>
        </tbody>
      </table>
    );
  }

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  c = controller;
  m = model;
  t = teacher;

  const modalContentChildren = [];

  const maxStudentsInRoom = useMaxRosterSize();

  if (!t.rosterSettingsFetched || maxStudentsInRoom.isLoading) {
    modalContentChildren.push(
      <Loader
        key="modalContentChildren-Loader-1"
        className="roster-file-loader"
        text={translate('Loading…')}
      />
    );
  } else {
    modalContentChildren.push(
      <div
        key="modalContentChildren-div-1"
        className={
          'roster-file-subtitle-container' +
          (m.uploading ? ' roster-file-subtitle-container-uploading' : '')
        }
      >
        <span className="roster-file-subtitle-text">
          {translate('Settings', true)}
        </span>
        <span
          className={
            'roster-file-change-text link' +
            (m.uploadErrors.length > 0 || (!m.importing && m.showExportSummary)
              ? ''
              : ' old-hidden')
          }
          onClick={c.changeClicked}
        >
          {translate('Change')}
        </span>
      </div>
    );

    if (m.uploadErrors.length > 0) {
      modalContentChildren.push(
        renderSettings(),
        <div key="modalContentChildren-div-2" className="roster-error-header">
          <AlertIcon className="roster-error-icon" />
          <span className="roster-error-header-text">
            {translate('Roster Error')}
          </span>
        </div>,
        <span
          key="modalContentChildren-span-1"
          className="roster-error-list-header"
        >
          {translate('Correct the following error(s) and import again:')}
        </span>
      );

      modalContentChildren.push(
        <ul key="modalContentChildren-ul-1" className="roster-error-list">
          {m.uploadErrors.map((errorString) => (
            <li key={errorString} className="roster-error-item">
              {errorString}
            </li>
          ))}
        </ul>
      );
    } else if (m.uploading) {
      modalContentChildren.push(
        renderSettings(),
        <span key="modalContentChildren-span-2" className="roster-filename">
          {m.fileName}
        </span>,
        <div key="modalContentChildren-div-3" className="progress-bar">
          <div className="progress" style={{ width: m.progress + '%' }} />
        </div>,
        <span
          key="modalContentChildren-span-4"
          className="progress-percent"
        >{`${m.progress}%`}</span>
      );
    } else {
      if (!m.importing && m.showExportSummary) {
        modalContentChildren.push(renderSettings());
      } else {
        modalContentChildren.push(
          <div
            key="modalContentChildren-div-4"
            className="roster-file-select-container"
          >
            <p className="roster-file-settings-label">
              {translate('Column Order')}
            </p>
            <Select onSelect={(name, value) => c.columnOrderChanged(value)}>
              {{
                title: translate(
                  'Last Name, First Name, Student ID, Email (Optional)'
                ),
                value: Constants.LAST_FIRST_ID,
                selected: m.importing
                  ? m.importOrder === Constants.LAST_FIRST_ID
                  : m.exportOrder === Constants.LAST_FIRST_ID,
              }}
              {{
                title: translate(
                  'First Name, Last Name, Student ID, Email (Optional)'
                ),
                value: Constants.FIRST_LAST_ID,
                selected: m.importing
                  ? m.importOrder === Constants.FIRST_LAST_ID
                  : m.exportOrder === Constants.FIRST_LAST_ID,
              }}
            </Select>
          </div>,
          <div
            key="modalContentChildren-div-5"
            className={
              'roster-file-select-container' +
              (m.importing ? ' roster-file-type-select-container' : '')
            }
          >
            <p className="roster-file-settings-label">
              {translate('File Type')}
            </p>
            <Select onSelect={(name, value) => c.fileTypeChanged(value)}>
              {{
                title: 'CSV',
                value: Constants.CSV,
                selected: m.importing
                  ? m.importType === Constants.CSV
                  : m.exportType === Constants.CSV,
              }}
              {{
                title: 'XLSX',
                value: Constants.XLSX,
                selected: m.importing
                  ? m.importType === Constants.XLSX
                  : m.exportType === Constants.XLSX,
              }}
            </Select>
          </div>
        );

        if (m.importing) {
          modalContentChildren.push(
            <a
              key="modalContentChildren-a-1"
              className="link roster-file-template-link"
              href={`${window.APP_CONFIG.STATIC_URL}${
                window.APP_CONFIG.VERSION
              }/data/roster_template.${
                m.importType === Constants.CSV ? 'csv' : 'xlsx'
              }`}
            >
              {translate('Download Template')}
            </a>
          );
        }

        modalContentChildren.push(
          <div key="modalContentChildren-div-6" id="includes-headers-container">
            <input
              className="vertical-align-middle"
              type="checkbox"
              id="includes-headers"
              checked={m.importing ? m.importHeader : m.exportHeader}
              onChange={c.includeHeaderChanged}
            />
            <label
              className="vertical-align-middle includes-headers-label"
              htmlFor="includes-headers"
            >
              {translate('Includes Column Headers')}
            </label>
          </div>
        );
      }
    }
  }

  return (
    <div id="roster-file-modal-background" className="modal open">
      <div id="roster-file-modal-box" className="modal-dialog">
        <div className="modal-header">
          <span className="popup-title">
            {m.importing
              ? translate('Import Roster')
              : translate('Export Roster')}
          </span>
          <div className="cancel-button" onClick={c.cancelClicked}>
            <CloseX className="cancel-button-x" color="#cccccc" />
          </div>
        </div>
        <div
          className={
            'roster-file-import-banner' +
            (!t.rosterSettingsFetched ||
            m.uploading ||
            !m.importing ||
            m.uploadErrors.length > 0
              ? ' old-hidden'
              : '')
          }
        >
          <InfoIcon className="roster-file-info-icon" />
          <span className="roster-file-import-message">
            {translate(
              'Importing a roster will clear all students from the {0} room.'
            ).format(m.roomName.toUpperCase())}
          </span>
        </div>
        <div id="roster-file-modal-content" className="form">
          {modalContentChildren}
        </div>
        <div
          id="roster-file-footer"
          className={
            'roster-file-footer form' +
            (!t.rosterSettingsFetched || m.uploading ? ' old-hidden' : '')
          }
        >
          <button
            className="roster-file-footer-button roster-file-cancel-button"
            onClick={c.cancelClicked}
          >
            {translate('Cancel', true)}
          </button>
          {m.importing ? (
            <Input
              accept={m.importType === Constants.CSV ? '.csv' : '.xls, .xlsx'}
              disabled={m.uploading}
              id="roster-upload"
              label={translate('Choose File', true)}
              type="file"
              refValue={fileInputRef}
              onChange={(event) => {
                c.fileChosen(event.target.files[0]);
                fileInputRef.current.value = null; // Clear the value so onChange() will fire if the same file is selected again.
                queryClient.invalidateQueries(roomsKeys.list());
              }}
            />
          ) : (
            <button
              id="roster-file-export-button"
              className="roster-file-footer-button roster-file-export-button"
              onClick={c.exportClicked}
            >
              {translate('Export', true)}
            </button>
          )}
          {m.importing ? (
            <div
              style={{
                paddingTop: 12,
                marginLeft: 25,
                fontFamily: 'Open Sans',
                fontSize: 12,
                fontStyle: 'italic',
                fontWeight: 400,
                letterSpacing: '0.01em',
                textAlign: 'left',
              }}
            >
              {translate('Maximum of {0} Students').format(
                maxStudentsInRoom.data ?? 0
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
